import React, { useCallback, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';

import Drawer from 'components/uiLibrary/Drawer';
import SelectDropdown from 'components/uiLibrary/Inputs/SelectDropdown';
import Loader from 'components/uiLibrary/Loader';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import WorkRoleListing from 'components/Work/Display/WorkRoleListing';

import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from 'constants/endpointConstants';
import { AGGREGATION_TYPES, TP, URL_SLUG_TYPES } from 'constants/index';

import { findCreditGroupByRole } from 'utils/casting';
import { getWorkRoleProfessions } from 'utils/common';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import { useQuery } from 'utils/react-query';
import { getWorkRoleLabel, getWorkTitle } from 'utils/works';

import searchQueries from 'containers/Search/queries';
import queries from 'containers/Work/queries';

import { useTranslation } from 'src/i18n';

import classes from './WorkRoleField.module.scss';

const RenderValue = ({ value, workId, showWorkComposer = false }) => {
  const { data: work } = useQuery(
    searchQueries.getSelectedOption({
      identifier: workId,
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        work_id: workId,
        aggregation_type: AGGREGATION_TYPES.WORK,
      },
      queryConfig: {
        enabled: showWorkComposer && !!workId,
      },
    }),
  );

  const workTitle = showWorkComposer ? getWorkTitle(work, true, true) : '';

  if (value?.name) {
    return (
      <span className={classes.value}>
        <Typography size={12} weight="bold">
          {getWorkRoleLabel(value)}
          {showWorkComposer && <span className={classes.workComposerName}>{`, ${workTitle}`}</span>}
          {value?.workRoleProfessions?.length > 0 && (
            <Typography size="12">{getWorkRoleProfessions(value?.workRoleProfessions)}</Typography>
          )}
        </Typography>
        {work?.name && (
          <Typography size="12" color="secondary">
            {work.original_name || work.name}
          </Typography>
        )}
      </span>
    );
  }

  return '';
};

const NoWorkMessage = ({ isDrawer }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  return (
    <Typography
      className={classnames(classes.noWorkMessage, { [classes.noWorkMessage__drawer]: isDrawer })}
      variant="p"
    >
      <SpriteIcon icon="warning" size={16} className={classes.noWorkMessage__icon} />
      {t(`${TP}.ROLE_DROPDOWN_NO_WORK_MESSAGE`)}
    </Typography>
  );
};

const WorkRoleField = ({
  value,
  workId,
  filters = {},
  onChange,
  onClick,
  styles,
  showWorkComposer = false,
  trackingData = {},
  isMobile,
  showLabel = true,
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const ref = useRef(null);

  useOnClickOutside(ref, event => {
    if (event?.target?.parentNode?.id === 'menu-select-roles') {
      setIsExpanded(false);
    }
  });

  const { data: castCrewData, isLoading } = useQuery(
    queries.getCastCrewDetails({
      filters: {
        work_id: workId,
        ...(filters?.[URL_SLUG_TYPES.DATE_FROM] && { [URL_SLUG_TYPES.DATE_FROM]: filters?.[URL_SLUG_TYPES.DATE_FROM] }),
      },
      queryConfig: { enabled: !!workId },
    }),
  );

  const matchedRole = useMemo(() => findCreditGroupByRole(castCrewData, value)?.role, [value, castCrewData]);

  const onChangeHandler = optionSelected => {
    setIsExpanded(false);
    setIsDrawerOpen(false);

    if (onChange) {
      onChange(optionSelected);
    }
  };

  const onOpenHandler = useCallback(() => {
    if (!isMobile) {
      setIsExpanded(true);
    }

    if (isMobile) {
      setIsDrawerOpen(true);
    }

    if (onClick) {
      onClick();
    }
  }, [isMobile, onClick]);

  const iconComponent = useMemo(
    () => <SpriteIcon icon={isExpanded ? 'keyboard_arrow_up' : 'expand_more'} size={20} className={classes.icon} />,
    [isExpanded],
  );

  return (
    <div className={classes.roleField}>
      {showLabel && (
        <Typography size={12} weight="medium" className={classes.roleField__title}>
          {t(`${TP}.FN_ROLE_TITLE`)}
        </Typography>
      )}
      <SelectDropdown
        name="select-roles"
        options={castCrewData}
        styles={{
          formControl: classnames(classes.formControl, { [styles?.formControl]: !!styles?.formControl }),
          select: classnames(classes.select, { [styles?.select]: !!styles?.select }),
          label: classnames(classes.label, { [styles?.label]: !!styles?.label }),
          paper: classnames(classes.paper, {
            [styles?.paper]: !!styles?.paper,
            [classes.paper__noWork]: !workId,
          }),
        }}
        onOpen={onOpenHandler}
        isOpen={isExpanded}
        value={matchedRole}
        renderValue={() => <RenderValue value={matchedRole} workId={workId} showWorkComposer={showWorkComposer} />}
        size="small"
        iconComponent={iconComponent}
        trackingData={trackingData}
      >
        {() => (
          <div ref={ref}>
            <WorkRoleListing
              role={matchedRole}
              workId={workId}
              filters={filters}
              onSelect={onChangeHandler}
              trackingData={trackingData}
            />
            {isLoading && workId && <Loader small className={classes.loader} />}
            {!workId && <NoWorkMessage />}
          </div>
        )}
      </SelectDropdown>
      <Drawer
        title={t(`${TP}.SELECT_ROLE_DRAWER_TITLE`)}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        className={classes.drawerContainer}
      >
        <WorkRoleListing
          roleId={matchedRole?.id}
          workId={workId}
          filters={filters}
          onSelect={onChangeHandler}
          trackingData={trackingData}
        />
        {isLoading && workId && <Loader className={classes.loader} />}
        {!workId && <NoWorkMessage isDrawer />}
      </Drawer>
    </div>
  );
};

export default WorkRoleField;
